import React from 'react';
import PublicLayout from '../components/public/PublicLayout';
import { PageHero } from './UniversitiesListPage';
import { Mail, Phone, MapPin } from 'lucide-react';

// Content adapted from the live EduBridge Network privacy policy.
// Layout mirrors TermsPage for a consistent professional feel.
const PrivacyPage = () => (
  <PublicLayout>
    <PageHero title="Privacy Policy" subtitle="Effective Date: April 07, 2025" />

    <section className="max-w-3xl mx-auto px-4 py-12" data-testid="privacy-content">
      <p className="text-gray-600 leading-relaxed">
        At Edu Bridge Network, your privacy matters to us. This Privacy Policy
        explains how we collect, use, share, and protect your information when
        you visit our website, create an account, or use any of our services.
      </p>

      <NumberedSection n="1" title="Information We Collect">
        <p className="mb-3">
          We collect both personal and non-personal information to deliver and
          improve our services.
        </p>
        <h3 className="font-semibold text-[#1A2A4F] mt-4 mb-2">a. Personal Information</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Personal details (name, email, phone number, date of birth, nationality).</li>
          <li>Academic information (transcripts, test scores, qualifications).</li>
          <li>Uploaded documents (passport, statement of purpose, CV, references).</li>
        </ul>
        <h3 className="font-semibold text-[#1A2A4F] mt-4 mb-2">b. Non-Personal Information</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Usage data such as IP address, device type, browser, and pages visited.</li>
          <li>Cookies and similar technologies used to remember your preferences.</li>
        </ul>
      </NumberedSection>

      <NumberedSection n="2" title="How We Use Your Information">
        <ul className="list-disc pl-6 space-y-1">
          <li>To process and progress your study-abroad applications.</li>
          <li>To match you with universities, programs, scholarships, and advisors.</li>
          <li>To communicate updates, reminders, and relevant offers.</li>
          <li>To improve our services through analytics and feedback.</li>
          <li>To comply with legal, regulatory, and audit obligations.</li>
        </ul>
      </NumberedSection>

      <NumberedSection n="3" title="Sharing of Information">
        <p className="mb-3">We may share your data with:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Partner universities, admission officers, and education consultants for the purpose of progressing your application.</li>
          <li>Trusted service providers who help us operate the platform (e.g. payment, email, and storage providers) under confidentiality agreements.</li>
          <li>Legal authorities, where required by law or to protect our users.</li>
        </ul>
        <p className="mt-3 font-semibold text-[#1A2A4F]">
          We never sell your personal data to advertisers or third parties.
        </p>
      </NumberedSection>

      <NumberedSection n="4" title="Cookies & Tracking Technologies">
        <ul className="list-disc pl-6 space-y-1">
          <li>We use cookies to remember your session, preferences, and to measure how our site is used.</li>
          <li>You can disable cookies through your browser settings, but some features of the platform may not function correctly.</li>
        </ul>
      </NumberedSection>

      <NumberedSection n="5" title="Data Security">
        <ul className="list-disc pl-6 space-y-1">
          <li>We apply industry-standard administrative, technical, and physical safeguards (encryption at rest and in transit, access controls, regular audits) to protect your information.</li>
          <li>No method of transmission over the internet is 100% secure; we cannot guarantee absolute security, but we work continuously to strengthen our defenses.</li>
        </ul>
      </NumberedSection>

      <NumberedSection n="6" title="Your Rights">
        <p className="mb-3">Subject to applicable law, you have the right to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Access, correct, or update your personal information.</li>
          <li>Request deletion of your account and associated data.</li>
          <li>Withdraw consent for marketing communications at any time.</li>
          <li>Lodge a complaint with a data-protection authority in your country.</li>
        </ul>
      </NumberedSection>

      <NumberedSection n="7" title="Children's Privacy">
        <ul className="list-disc pl-6 space-y-1">
          <li>Our platform is intended for users aged 16 and above.</li>
          <li>If you are between 16 and 18, you must use the platform with the consent of a parent or guardian.</li>
          <li>We do not knowingly collect data from children under 16.</li>
        </ul>
      </NumberedSection>

      <NumberedSection n="8" title="Changes to This Policy">
        <ul className="list-disc pl-6 space-y-1">
          <li>We may update this Privacy Policy from time to time. The latest version will always be posted on this page with an updated Effective Date.</li>
          <li>For significant changes, we will notify you via email or an in-app notice.</li>
        </ul>
      </NumberedSection>

      <NumberedSection n="9" title="Contact Us">
        <p className="mb-4">
          If you have any questions, requests, or concerns about this Privacy
          Policy or how we handle your data, please contact us:
        </p>
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 space-y-3 text-sm">
          <p className="font-bold text-[#1A2A4F] text-base">Edu Bridge Network</p>
          <p className="flex items-center gap-2 text-gray-700">
            <Mail className="w-4 h-4 text-[#F5A623] shrink-0" />
            <a href="mailto:edubridgenetwork@gmail.com" className="hover:text-[#F5A623] hover:underline">edubridgenetwork@gmail.com</a>
          </p>
          <p className="flex items-center gap-2 text-gray-700">
            <Phone className="w-4 h-4 text-[#F5A623] shrink-0" />
            <a href="tel:+985235547" className="hover:text-[#F5A623] hover:underline">+9 8523 5547</a>
          </p>
          <p className="flex items-start gap-2 text-gray-700">
            <MapPin className="w-4 h-4 text-[#F5A623] shrink-0 mt-0.5" />
            <span>
              Maple Visa Services Inc.<br />
              525 Bay Street, Suite 700<br />
              Toronto, ON M5G 2L2, Canada
            </span>
          </p>
        </div>
      </NumberedSection>
    </section>
  </PublicLayout>
);

const NumberedSection = ({ n, title, children }) => (
  <div className="mt-10">
    <h2 className="text-xl font-bold text-[#1A2A4F]">
      {n}. {title}
    </h2>
    <div className="mt-3 text-gray-600 leading-relaxed">{children}</div>
  </div>
);

export default PrivacyPage;
